/**
 * 会場データの読み込み。
 *
 * `public/venues/` に置かれた実データ（IGC・KML・map.txt）を取得し、
 * gv-core で解析して画面が扱える形にする。
 * **解析はすべてブラウザ側で行う**ので、利用者が自分の IGC を読み込む経路と同じ道を通る。
 */

import {
  extractIgcTextFromZip,
  igcToFixSamples,
  kmlToMarkPoints,
  looksLikeZip,
  parseAnyMapCalibration,
  parseIgc,
  parseKmlPlacemarks,
  type FixSample,
  type MapCalibration,
  type MarkPoint,
} from '@gv4d/core';

export interface VenueManifest {
  id: string;
  name: string;
  files: {
    calibration?: string;
    goals?: string;
    pzsa?: string;
    hazards?: string;
    notam?: string;
    compMap?: string;
  };
  flights: Array<{ file: string; label: string }>;
}

/**
 * 競技地図タイルのマニフェスト（`scripts/build-compmap-tiles.mjs` が書き出す
 * `compmap.json` の中身）。タイル本体は同じディレクトリの
 * `compmap/{z}/{x}/{y}.jpg` に置かれる（マニフェスト自体はURLを持たない）。
 */
export interface CompMapManifest {
  bounds: { west: number; south: number; east: number; north: number };
  minzoom: number;
  maxzoom: number;
  tileSize: number;
  attribution: string;
}

interface PzSaZoneBase {
  id: string;
  type: 'PZ' | 'SA';
  /** 表示名（"No.10" や施設名） */
  label: string;
  /** 高度制限 [ft]。指定が無ければ null（離着陸禁止など高度に関係しない注意） */
  altFt: number | null;
  kind: string;
  note: string;
}

/** 円（8デジット＋半径で特定できるもの） */
export interface PzSaCircleZone extends PzSaZoneBase {
  shape: 'circle';
  lat: number;
  lon: number;
  radiusM: number;
}

/** 矩形などの多角形（原典が「地図参照」としている、円で表せない区域） */
export interface PzSaPolygonZone extends PzSaZoneBase {
  shape: 'polygon';
  /** 頂点を順番に並べたもの（閉じなくてよい。描画時に始点へ戻す） */
  points: Array<{ lat: number; lon: number }>;
}

/**
 * 線状の構造物（鉄道トンネルなど）を「境界」ではなく中心線＋半径で表す
 * 区域（例: 佐賀PZ-15、線路の上に形成される半円状のトンネル）。
 *
 * ★ polygon（手で境界点を指定する）で近似すると、本来は中心線上でしか
 *   altFt（頂点の高さ）に達しない半円状の断面を、幅いっぱいまで一様に
 *   altFtの壁として描いてしまい、利用者に手作業で帯状ポリゴンの頂点を
 *   作らせる必要が出る。中心線＋半径という原典の説明そのままの形で
 *   持たせ、帯状ポリゴンへの変換（レンダリング側の責務）をコード側で
 *   行う（layers.ts の pzSaPolygonsToGeoJson 参照）。
 */
export interface PzSaTubeZone extends PzSaZoneBase {
  shape: 'tube';
  /** 中心線（開いた折れ線、2点以上） */
  centerline: Array<{ lat: number; lon: number }>;
  /** 中心線からの半径 [m]（断面は半径radiusMの半円） */
  radiusM: number;
}

/** PZ（禁止・要注意区域）/ SA（要注意エリア）1件 */
export type PzSaZone = PzSaCircleZone | PzSaPolygonZone | PzSaTubeZone;

export interface PzSaData {
  /** 情報の出所（原典ページのURLなど） */
  source: string;
  title: string;
  /** 原典側の最終更新日。情報が古くなっていないか利用者が判断できるように残す */
  lastUpdate: string;
  zones: PzSaZone[];
}

/**
 * NOTAM由来の高度制限区域（通称BluePZ）。曜日・時間帯によって区域の
 * 分割・高度上限が切り替わる会場がある（渡良瀬など）ため、PZ/SAより
 * 一段複雑な構造を持つ。ジオメトリ・侵入判定などの純粋計算は
 * @gv4d/core の notam.ts 側にあり、ここは会場データの形だけを持つ。
 *
 * ★ 「入間基地と事前調整済み」のような条件は渡良瀬BluePZ固有の個別
 * 事情であり、専用フィールド・専用UIとしてスキーマに焼き込まない。
 * ゾーンが任意個の「条件付き高度引き上げ」(overrides) を持てる汎用的な
 * 仕組みにし、文言・対象体制・引き上げ後の高度は全てデータ側が持つ。
 */
export interface NotamAltitudeOverride {
  /** グローバルにユニーク */
  id: string;
  /** チェックボックスの表示文言（例:「入間基地と事前調整済み」）。UI側はこの文字列をそのまま出すだけ */
  label: string;
  /** 有効化時に使う高度上限 [ft MSL] */
  altFt: number;
  /** 指定時はその体制がアクティブな時だけ有効。未指定なら常に候補になる */
  appliesToRegimeId?: string;
}

export interface NotamZone {
  id: string;
  label: string;
  /** 通常時の高度上限 [ft MSL] */
  altFt: number;
  /** 条件付きで引き上げ可能な高度（0件以上） */
  overrides?: NotamAltitudeOverride[];
  /** 開いた頂点列（描画時に始点へ戻す。PzSaPolygonZoneと同じ規約） */
  points: Array<{ lat: number; lon: number }>;
  note: string;
  /** チャート画像からの目視転記であることの注記。運用前の最終確認を促す */
  topologyNote?: string;
}

/** 曜日・時間帯で切り替わる「体制」1つぶん（例: 平日+土曜午前） */
export interface NotamRegime {
  id: string;
  label: string;
  zones: NotamZone[];
}

/**
 * 飛行場中心の参考円（例: 宇都宮飛行場5NM/10NM/15NM）。原典チャートには
 * BluePZの高度制限区域（実線）とは別に、航空路誌的な参考情報として
 * 破線の同心円が重ねて描かれている。地図上に表示するだけの参考情報で、
 * 高度制限区域ではないため侵入警告（checkNotamIncursion）の対象にはしない。
 */
export interface NotamReferenceCircle {
  label: string;
  center: { lat: number; lon: number };
  radiusNm: number;
}

export interface NotamAreaData {
  source: string;
  title: string;
  lastUpdate: string;
  /** 各体制で共通の外周（参考表示用。ゾーンの当たり判定には使わない） */
  outerBoundary: Array<{ lat: number; lon: number }>;
  /** 平日・土曜午前に使う体制のid */
  weekdayRegimeId: string;
  /** 土曜午後・日祝に使う体制のid */
  weekendRegimeId: string;
  /** 土曜のAM/PM切り替え時刻（JSTの時、0-23）。省略時は12 */
  saturdayCutoffHourJst?: number;
  regimes: NotamRegime[];
  /** 飛行場中心の参考円（省略可）。侵入警告の対象外 */
  referenceCircles?: NotamReferenceCircle[];
}

export interface Venue {
  id: string;
  name: string;
  calibration: MapCalibration | null;
  goals: MarkPoint[];
  flights: LoadedFlight[];
  pzsa: PzSaData | null;
  /** PZ/SAの元ファイル名。会場切り替えのたびに表示し直す */
  pzsaFileName: string | null;
  /**
   * 送電線・鉄道・道路（scripts/build-hazard-lines.mjs で OpenStreetMap から
   * 切り出したもの）。標準的なGeoJSONなので、変換せずそのまま地図のソースに渡せる。
   */
  hazards: GeoJSON.FeatureCollection | null;
  /** NOTAM高度制限区域。会場に複数のエリアがあり得るため配列（渡良瀬は1件） */
  notam: NotamAreaData[] | null;
  notamFileName: string | null;
  /** 競技地図タイルのマニフェスト。未生成の会場では null（既定オフのオーバーレイなので必須ではない） */
  compMap: CompMapManifest | null;
  /**
   * 個別ファイルの取得・解析に失敗した項目（オフラインで未キャッシュだった場合など）。
   * `loadVenue()` はこれらを venue 全体の失敗にはせず、該当データを null/空で
   * 埋めて処理を続ける。空配列なら欠落なし
   */
  warnings: string[];
}

export interface LoadedFlight {
  label: string;
  terminalId: string;
  fixes: FixSample[];
  /** 記録計の情報（IGC ヘッダ由来） */
  recorder: string;
  /**
   * IGCヘッダ（HFATS）に記録されていたQNH[hPa]。記録計が対応していない
   * ことも多いので無ければundefined。`fixes`の`altPressure`は常に
   * 標準気圧(1013.25hPa)基準の生値のまま（QNH補正は一切していない）。
   * 補正は main.ts 側で state.qnhHpa を使って別途行う（この値はその
   * 初期値の候補として使うだけ）
   */
  qnh?: number;
}

const base = `${import.meta.env.BASE_URL}venues`;

export async function loadVenueList(): Promise<VenueManifest[]> {
  const res = await fetch(`${base}/index.json`);
  if (!res.ok) throw new Error(`会場一覧を取得できません (HTTP ${res.status})`);
  return res.json();
}

/** Shift-JIS のコメントが混ざるファイルを壊さずに読む */
async function fetchLatin1(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} を取得できません (HTTP ${res.status})`);
  const buf = await res.arrayBuffer();
  return new TextDecoder('latin1').decode(buf);
}

async function fetchUtf8(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url} を取得できません (HTTP ${res.status})`);
  return res.text();
}

/**
 * `fn` を試み、失敗しても venue 全体を巻き込まずに `fallback` へ逃がす。
 *
 * ★ なぜ要るか: 以前は1ファイルの取得失敗（例: `.igc` はサイズが大きいため
 *   事前キャッシュ対象から除外しており、オフラインでは失敗しうる）が
 *   `loadVenue()` 全体を reject させ、`state.venue` が一切設定されなかった。
 *   その結果、起動時に「オフライン地図を取得済みなら既定にする」処理
 *   （main.ts の boot()）が `state.venue` を前提にしているため素通りし、
 *   **オフライン地図をダウンロード済みでも「地図なし」のまま起動する**
 *   不具合になっていた。1ファイルの欠落が他の全データを道連れにしない
 *   ようにする。
 */
async function safe<T>(label: string, fn: () => Promise<T>, warnings: string[]): Promise<T | null> {
  try {
    return await fn();
  } catch (e) {
    warnings.push(`${label}: ${(e as Error).message}`);
    return null;
  }
}

export async function loadVenue(manifest: VenueManifest): Promise<Venue> {
  const dir = `${base}/${manifest.id}`;
  const warnings: string[] = [];

  const calibration = manifest.files.calibration
    ? await safe(
        '競技地図の較正データ',
        async () => parseAnyMapCalibration(await fetchLatin1(`${dir}/${manifest.files.calibration}`)),
        warnings,
      )
    : null;

  const goals =
    (manifest.files.goals
      ? await safe(
          'ゴール地点',
          async () => kmlToMarkPoints(parseKmlPlacemarks(await fetchUtf8(`${dir}/${manifest.files.goals}`))),
          warnings,
        )
      : []) ?? [];

  // goals.kml・map.txt と同じく venues/**/*.json として Service Worker の
  // 事前キャッシュ対象に入る（vite.config.ts）。つまり「最新のファイルに
  // 差し替える」は、他の会場データと同様にアプリを更新（ビルドし直して
  // デプロイ・利用者側で「更新する」を押す）することで反映される。
  // ここでは常にそのビルド時点の最新ファイルを読みに行くだけでよい
  const pzsa = manifest.files.pzsa
    ? await safe(
        'PZ/SA',
        async () => {
          const res = await fetch(`${dir}/${manifest.files.pzsa}`);
          return res.ok ? ((await res.json()) as PzSaData) : null;
        },
        warnings,
      )
    : null;

  const hazards = manifest.files.hazards
    ? await safe(
        '送電線・鉄道等の障害物',
        async () => {
          const res = await fetch(`${dir}/${manifest.files.hazards}`);
          return res.ok ? ((await res.json()) as GeoJSON.FeatureCollection) : null;
        },
        warnings,
      )
    : null;

  const notam = manifest.files.notam
    ? await safe(
        'NOTAM高度制限区域',
        async () => {
          const res = await fetch(`${dir}/${manifest.files.notam}`);
          return res.ok ? ((await res.json()) as NotamAreaData[]) : null;
        },
        warnings,
      )
    : null;

  const compMap = manifest.files.compMap
    ? await safe(
        '競技地図タイル',
        async () => {
          const res = await fetch(`${dir}/${manifest.files.compMap}`);
          return res.ok ? ((await res.json()) as CompMapManifest) : null;
        },
        warnings,
      )
    : null;

  const flights: LoadedFlight[] = [];
  for (const f of manifest.flights ?? []) {
    const loaded = await safe(
      `航跡「${f.label}」`,
      async () => {
        const igc = parseIgc(await fetchUtf8(`${dir}/${f.file}`));
        const fixes = igcToFixSamples(igc);
        return fixes.length > 0 ? { igc, fixes } : null;
      },
      warnings,
    );
    if (!loaded) continue;
    flights.push({
      label: f.label,
      terminalId: loaded.fixes[0].terminalId,
      fixes: loaded.fixes,
      recorder: loaded.igc.header.frType ?? loaded.igc.header.manufacturer,
    });
  }

  return {
    id: manifest.id,
    name: manifest.name,
    calibration,
    goals,
    flights,
    pzsa,
    pzsaFileName: pzsa ? manifest.files.pzsa! : null,
    hazards,
    notam,
    notamFileName: notam ? manifest.files.notam! : null,
    compMap,
    warnings,
  };
}

/**
 * LINEなどのメッセージアプリはIGCファイルをそのまま送れず、送り手がzipに
 * してから送ってくることがある。スマホでは受け取ったzipをその場で展開する
 * 手段が無いことが多いので、拡張子に関係なく中身の先頭バイトでzipかどうか
 * を判定し、zipならその場でIGCエントリを取り出して読む。
 */
async function readIgcText(file: File): Promise<string> {
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  if (!looksLikeZip(head)) return file.text();
  const bytes = new Uint8Array(await file.arrayBuffer());
  return extractIgcTextFromZip(bytes);
}

/** IGC ファイルを利用者が直接読み込む経路（zipに包まれていてもそのまま読める） */
export async function loadFlightFromFile(file: File): Promise<LoadedFlight> {
  const igc = parseIgc(await readIgcText(file));
  const fixes = igcToFixSamples(igc);
  if (fixes.length === 0) throw new Error('有効な測位が含まれていません');
  return {
    label: file.name,
    terminalId: fixes[0].terminalId,
    fixes,
    recorder: igc.header.frType ?? igc.header.manufacturer,
    qnh: igc.header.qnh,
  };
}
